/**
 * @fileoverview WIP ledger row shape and Excel export (MOR-906 §7.4)
 *
 * Purpose: the non-visual half of the WIP ledger — the row shape the query
 * returns, how a provision method reads, and the workbook writer. Kept beside
 * the table for the same reason the pivot export is (see pivotExport.ts): the
 * sheet has to be the grid on screen, column for column.
 *
 * The workbook has two sheets. "WIP" is the matter summary — the table as it
 * reads on screen. "Entries" is every one of those matters' underlying time,
 * flat with the matter on each row: on screen that detail hides behind an
 * expander one matter at a time, but a flat sheet is what lets a reviewer
 * filter and pivot it, and a summary that cannot be drilled into cannot be
 * checked.
 *
 * Two deliberate differences from the screen, because a spreadsheet is not a
 * screen. Provisions are written as negative numbers so Gross + Provision =
 * Net adds up in Excel rather than reading as "($403)". And the asterisk that
 * marks unrated time becomes its own column, since a footnote glyph means
 * nothing once the data leaves the page.
 *
 * Used by:
 * - src/components/timesheets/WipLedger.tsx
 */

import * as XLSX from "xlsx";

import { unitsToHours } from "./format";

/** The live provision on a matter, as the ledger reports it. */
export interface WipLedgerProvision {
  method: string;
  percentageRate?: number;
  /** Null when a method is still missing an input. */
  amount: number | null;
  unavailable?: string;
  clamped: boolean;
}

/** One matter's row in the ledger. */
export interface WipLedgerMatter {
  jobId: string;
  jobName: string;
  jobCode?: string;
  units: number;
  /** Gross WIP: unbilled approved billable value, before provisioning. */
  value: number;
  netValue: number;
  unratedUnits: number;
  writtenOffValue: number;
  provision: WipLedgerProvision | null;
  oldestDate: string;
  aging: {
    current: number;
    days31to60: number;
    days61to90: number;
    over90: number;
  };
}

export interface WipLedgerTotals {
  units: number;
  value: number;
  netValue: number;
  provisionValue: number;
  writtenOffValue: number;
}

/** One time entry behind a matter's WIP, as the drill-down reports it. */
export interface WipLedgerEntry {
  jobName: string;
  jobCode?: string;
  date: string;
  staff: string;
  /** IPS code from the rate snapshot, absent on time approved without a rate. */
  staffCode?: string;
  categoryName: string;
  narrative: string;
  units: number;
  /** Absent when no rate resolved: the hours count, the value does not. */
  value?: number;
  projectCode: "pre" | "post";
  writtenOff: boolean;
  writeOffReason?: string;
}

/** How a provision was arrived at, short enough to sit under a figure. */
export function provisionMethodLabel(provision: WipLedgerProvision): string {
  switch (provision.method) {
    case "fixed_dollar":
      return "fixed";
    case "percentage":
      return `${provision.percentageRate ?? 0}% of WIP`;
    case "net_approval_cap":
      return "net of cap";
    case "net_estate_cash":
      return "net of est. funds";
    default:
      return "";
  }
}

const COLUMNS = [
  "Matter",
  "Code",
  "Hours",
  "Unrated hours",
  "Gross WIP",
  "Provision",
  "Provision basis",
  "Net WIP",
  "Written off",
  "0–30d",
  "31–60d",
  "61–90d",
  "90d+",
  "Oldest entry",
] as const;

/** Blank rather than a zero, so a sparse sheet reads as one. */
function money(value: number): number | string {
  return value === 0 ? "" : Number(value.toFixed(2));
}

function hours(units: number, unitMinutes: number): number | string {
  const value = unitsToHours(units, unitMinutes);
  return value === 0 ? "" : Number(value.toFixed(2));
}

function toAoa(args: {
  matters: ReadonlyArray<WipLedgerMatter>;
  totals: WipLedgerTotals;
  unitMinutes: number;
  today: string;
  /** Set when a matter filter is applied, so the sheet says so. */
  filterNote?: string;
}): (string | number)[][] {
  const { matters, totals, unitMinutes, today, filterNote } = args;

  const body = matters.map((m) => [
    m.jobName,
    m.jobCode ?? "",
    hours(m.units, unitMinutes),
    hours(m.unratedUnits, unitMinutes),
    money(m.value),
    // Negative, so the three WIP columns reconcile in the sheet.
    m.provision?.amount ? money(-m.provision.amount) : "",
    m.provision === null
      ? ""
      : m.provision.amount === null
        ? "Needs input"
        : `${provisionMethodLabel(m.provision)}${m.provision.clamped ? " (capped at gross)" : ""}`,
    money(m.netValue),
    money(m.writtenOffValue),
    money(m.aging.current),
    money(m.aging.days31to60),
    money(m.aging.days61to90),
    money(m.aging.over90),
    m.oldestDate,
  ]);

  const totalsRow: (string | number)[] = [
    filterNote ? "Total (filtered)" : "Total",
    "",
    hours(totals.units, unitMinutes),
    "",
    money(totals.value),
    totals.provisionValue ? money(-totals.provisionValue) : "",
    "",
    money(totals.netValue),
    money(totals.writtenOffValue),
    "",
    "",
    "",
    "",
    "",
  ];

  return [
    ["Work in progress"],
    [`As at ${today}`],
    ...(filterNote ? [[filterNote]] : []),
    [],
    [...COLUMNS],
    ...body,
    totalsRow,
  ];
}

const ENTRY_COLUMNS = [
  "Matter",
  "Code",
  "Date",
  "Staff",
  "Staff code",
  "Category",
  "Project code",
  "Narrative",
  "Hours",
  "Value",
  "Unrated",
  "Written off",
  "Write-off reason",
] as const;

function toEntriesAoa(args: {
  entries: ReadonlyArray<WipLedgerEntry>;
  unitMinutes: number;
  today: string;
  filterNote?: string;
}): (string | number)[][] {
  const { entries, unitMinutes, today, filterNote } = args;

  const body = entries.map((e) => [
    e.jobName,
    e.jobCode ?? "",
    e.date,
    e.staff,
    e.staffCode ?? "",
    e.categoryName,
    e.projectCode === "pre" ? "Pre-appointment" : "Post-appointment",
    e.narrative,
    hours(e.units, unitMinutes),
    e.value === undefined ? "" : money(e.value),
    e.value === undefined ? "Yes" : "",
    e.writtenOff ? "Yes" : "",
    e.writeOffReason ?? "",
  ]);

  return [
    ["Work in progress — time detail"],
    [`As at ${today}`],
    ...(filterNote ? [[filterNote]] : []),
    // Written-off time sits in the ledger's "Written off" column, not in
    // gross WIP, so the two sheets only reconcile once it is excluded.
    ["Written-off entries are listed but excluded from gross WIP."],
    [],
    [...ENTRY_COLUMNS],
    ...body,
  ];
}

/**
 * Download the ledger as a workbook: matter summary, then the time behind it.
 *
 * `entries` is fetched on click rather than subscribed to, so it may be
 * omitted — a summary-only sheet beats failing the export outright.
 */
export function exportWipLedgerToExcel(args: {
  matters: ReadonlyArray<WipLedgerMatter>;
  totals: WipLedgerTotals;
  entries?: ReadonlyArray<WipLedgerEntry>;
  unitMinutes: number;
  today: string;
  filterNote?: string;
}): void {
  const sheet = XLSX.utils.aoa_to_sheet(toAoa(args));
  sheet["!cols"] = [
    { wch: 32 },
    { wch: 10 },
    ...Array.from({ length: COLUMNS.length - 3 }, () => ({ wch: 13 })),
    { wch: 13 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "WIP");

  if (args.entries) {
    const detail = XLSX.utils.aoa_to_sheet(
      toEntriesAoa({
        entries: args.entries,
        unitMinutes: args.unitMinutes,
        today: args.today,
        filterNote: args.filterNote,
      }),
    );
    detail["!cols"] = [
      { wch: 32 },
      { wch: 10 },
      { wch: 12 },
      { wch: 22 },
      { wch: 12 },
      { wch: 18 },
      { wch: 17 },
      { wch: 60 },
      { wch: 9 },
      { wch: 11 },
      { wch: 9 },
      { wch: 11 },
      { wch: 40 },
    ];
    XLSX.utils.book_append_sheet(wb, detail, "Entries");
  }

  XLSX.writeFile(
    wb,
    `WIP as at ${args.today}.xlsx`.replace(/[/\\?%*:|"<>]/g, "-"),
  );
}
